import type { PlanCode, PlanEntitlement } from "../../domain";

const EMPTY_ENTITLEMENT: PlanEntitlement = {
  code: "visitor",
  label: "Visitor",
  monthlyCents: 0,
  quarterlyCents: 0,
  annualCents: 0,
  publicChapterAccess: "preview",
  publicTextAccess: "metered",
  publicMediaAccess: "none",
  privateChaptersPerMonth: 0,
  githubDeepDive: false,
  aiCreate: false,
  priorityQueue: false,
  canSubmitCommunityIdea: false,
  canVoteCommunityIdea: false,
  canCommentCommunityIdea: false,
  canCreateAiCourse: false,
};

/**
 * Client-side plan store. Mirrors the server-side planCache but lives
 * inside the prototype SPA. Populated by hydratePrototypeRuntime once
 * the bootstrap endpoint returns.
 *
 *   const plans = await state.runtime.api.bootstrap(plan);
 *   planStore.setPlans(plans);
 *
 * Synchronous reads are mandatory because the existing call sites
 * (commerceController.paintPaywallPrices, homeStateController) are
 * hot-paint functions that cannot await without rewriting all
 * markup-binding code. Bootstrap runs once before any of these
 * paint functions are reached (they live behind auth-gated
 * navigation or paywall opens), so the read can stay sync.
 *
 * Fallback: if a read happens before bootstrap completes (e.g. a
 * unit test, or a dev hot-reload), the TS planCatalog is returned
 * as a last-resort safety net. The TS catalog is now a "shoebox
 * fallback" — it is no longer the source of truth but it keeps
 * the prototype from going blank during the bootstrap window.
 */

let _plans: Record<PlanCode, PlanEntitlement> | null = null;
let _catalogExpiresAt = 0;

export const planStore = {
  setPlans(plans: PlanEntitlement[] | Record<PlanCode, PlanEntitlement>, metadata?: { catalogExpiresAt?: string | null }): void {
    if (Array.isArray(plans)) {
      const out: Record<string, PlanEntitlement> = {};
      for (const p of plans) out[p.code] = p;
      _plans = out as Record<PlanCode, PlanEntitlement>;
    } else {
      _plans = plans;
    }
		const expiresAt = metadata?.catalogExpiresAt ? Date.parse(metadata.catalogExpiresAt) : NaN;
		_catalogExpiresAt = Number.isFinite(expiresAt) ? expiresAt : 0;
  },

  getPlanEntitlements(plan: PlanCode): PlanEntitlement {
    if (_plans && _plans[plan]) return _plans[plan];
    // Cold read (e.g. pre-bootstrap unit test, or a hot-reload race):
    // return an EMPTY entitlement that denies everything. This is
    // safer than the old TS planCatalog fallback because the EMPTY
    // does not pretend a plan has capabilities it does not have.
    return { ...EMPTY_ENTITLEMENT, code: plan, label: plan };
  },

  getAll(): Record<PlanCode, PlanEntitlement> | null {
    return _plans;
  },

	isExpired(now = Date.now()): boolean {
		// Older bootstrap payloads did not carry freshness metadata. Keep a
		// complete legacy catalog usable for ordinary purchase surfaces; the
		// GitHub policy path explicitly forces its strict refresh instead.
		return _catalogExpiresAt > 0 && _catalogExpiresAt <= now;
	},

  clear(): void {
    _plans = null;
		_catalogExpiresAt = 0;
  },
};

// Re-export the sync getter under the historical name so the
// commerce/homeState controllers can switch their import line with
// minimal churn. They previously imported
//   import { getPlanEntitlements } from "../../domain/plans/planCatalog";
// After this change:
//   import { getPlanEntitlements } from "./planStore";
// The signature is identical (sync, PlanCode → PlanEntitlement).
export function getPlanEntitlements(plan: PlanCode): PlanEntitlement {
  return planStore.getPlanEntitlements(plan);
}
